/**
 * Converts an Excel serial date number to a JavaScript Date object.
 * Excel serial dates are the number of days since 1900-01-00 (with some quirks).
 *
 * @param serial - Excel serial date number (0 to 2958465)
 * @returns JavaScript Date object
 * @throws Error if input is invalid or out of range
 */
export function excelSerialToJsDate(serial) {
    if (typeof serial !== 'number' || isNaN(serial)) {
        throw new Error('Invalid input: must be a number');
    }
    // Excel dates range from 0 to 2958465 (1900-01-01 to 9999-12-31)
    if (serial < 0 || serial > 2958465) {
        throw new Error('Date out of range: must be between 0 and 2958465');
    }
    const msPerDay = 86400000;
    const excelEpoch = Date.UTC(1899, 11, 30);
    const leapBugCutoff = 61;
    const adjusted = serial < leapBugCutoff ? serial - 1 : serial;
    const date = new Date(excelEpoch + adjusted * msPerDay);
    if (isNaN(date.getTime())) {
        throw new Error('Invalid resulting date');
    }
    return date;
}
/**
 * Reverse of excelSerialToJsDate – turns a JS Date into an Excel
 * serial number, honouring the 1900-02-29 “leap-year bug”.
 *
 * @param   date  Date | number | string – anything new Date(…) accepts
 * @returns number – the Excel serial (may contain a fractional time part)
 * @throws  Error  – on invalid input or out-of-range dates
 */
export function jsDateToExcelSerial(date) {
    const d = new Date(date);
    // Basic validation
    if (isNaN(d.getTime())) {
        throw new Error('Invalid input: must resolve to a Date');
    }
    // Truncate to UTC midnight so that serials round-trip with the forward func.
    // (Remove this block if you *want* the time-of-day fraction.)
    d.setUTCHours(0, 0, 0, 0);
    const msPerDay = 86_400_000; // 24*60*60*1000
    const excelEpoch = Date.UTC(1899, 11, 30); // Excel day-zero
    const leapBugCutoff = 61; // 1900-02-29 = serial 60
    // Days (and fractional days) since Excel epoch
    const adjusted = (d.getTime() - excelEpoch) / msPerDay;
    // Re-inject the leap-year bug: for dates before 1900-03-01
    // Excel’s serial is one greater than the real count of days.
    const serial = adjusted < leapBugCutoff ? adjusted + 1 : adjusted;
    // Range check to mirror the forward converter
    if (serial < 0 || serial > 2_958_465) {
        // 9999-12-31
        throw new Error('Date out of range: must be between 1899-12-30 and 9999-12-31');
    }
    return serial;
}
export function javascriptDateToYYYYMMDD(date) {
    return date.toISOString().split('T')[0];
}
export function javascriptDateToMMDDYYYY(date) {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
}
export function isIsoDate(s) {
    if (typeof s !== 'string')
        return false;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m)
        return false;
    const iso = `${m[1]}-${m[2]}-${m[3]}T00:00:00Z`;
    const d = new Date(iso);
    return !Number.isNaN(d.getTime()) && d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
//# sourceMappingURL=date-helpers.js.map